// ============================================================================
// RaidLayout — Focus-consistent rail+stage shell for the Co-Op Raid section.
// ----------------------------------------------------------------------------
// The rail (left) holds the Forge form when idle, or the arena switcher when a
// raid is live. The stage (right) renders the
// active arena (Battlefield / War Room / Constellation / Timeline strip),
// the target detail card, the live audit ledger and past-raid history.
//
// Every count that used to read TimelineElement.totalChecklistCount now comes
// from raidTargeting.computeRaidTargetStats, which walks the real subtree so
// GOAL / TASK / SUBTASK targets all work post-checklist-books cutover.
// ============================================================================

import { useEffect, useMemo, useState, type FormEvent, type JSX } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import HubRoundedIcon from '@mui/icons-material/HubRounded'
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'
import { useRaid } from '../../context/useRaid'
import { useTimeline } from '../../context/useTimeline'
import { useWorkspace } from '../../context/useWorkspace'
import { roleCanPerform } from '../../utils/workspacePermissions'
import { isCurseActive } from '../../utils/raidCurse'
import type { ActiveRaidModel, RaidView } from '../../types/schema'
import { PENALTY_RAID_REWARD_MULTIPLIER, BOSS_CURSE_BREAK_CREDIT_COST } from '../../utils/gamificationRegistry'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { Checkbox, NumberStepper, RailPageShell, Select, TextInput } from '../Common'
import type { SelectOption } from '../Common/Select'
import { EnrageTimer } from './EnrageTimer'
import { RaidStatusBanner } from './RaidStatusBanner'
import { PastRaidsSection } from './PastRaidsSection'
import { renderRaidArena } from './RaidArenas'
import { buildRaidTargetOptions, computeRaidTargetStats, describeHpFormula, type RaidTargetOption } from '../../utils/raidTargeting'
import {
  normalizeRaidBlueprint,
  previewBossHp,
  REWARD_SPLIT_LABELS,
} from '../../utils/raidBlueprint'
import { testingOverrideAllowsAllFeatures } from '../../config/testingOverrides'
import { useNls } from '../../nls/nlsProvider'
import { getRaidArenaMeta, type ExperienceNlsT } from '../ExperienceNls'
import styles from './Raid.module.css'

type ArenaKey = 'BATTLEFIELD' | RaidView

const ARENA_ICON: Record<ArenaKey, JSX.Element> = {
  BATTLEFIELD: <ShieldRoundedIcon fontSize="inherit" />,
  WAR_ROOM: <BoltRoundedIcon fontSize="inherit" />,
  CONSTELLATION: <HubRoundedIcon fontSize="inherit" />,
  TIMELINE: <TimelineRoundedIcon fontSize="inherit" />,
}

const TARGET_KIND_LABEL = (option: RaidTargetOption): string => {
  const kind = option.kind === 'GOAL' ? '◎ Goal' : option.kind === 'TASK' ? '▣ Task' : '▸ Subtask'
  return option.parentTitle ? `${kind} · ${option.title}  —  in ${option.parentTitle}` : `${kind} · ${option.title}`
}

export const RaidLayout = () => {
  const { t } = useNls()
  const { activeRaid, activeCurse, startRaid, resolveRaid } = useRaid()
  const { elements, checklistItems } = useTimeline()
  const { activeRole, activeWorkspaceId, canPerform, workspaces } = useWorkspace()
  const canManageRaids = canPerform('raid.manage')
  const isAdmin = roleCanPerform(activeRole, 'ADMIN') || testingOverrideAllowsAllFeatures()

  // --------------------------------------------------------------------------
  // Admin-tunable blueprint. Editing now lives in Workspace Settings > Games;
  // Raid consumes the workspace doc field for forge defaults and previews.
  // --------------------------------------------------------------------------
  const activeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null,
    [activeWorkspaceId, workspaces],
  )
  const blueprint = useMemo(
    () => normalizeRaidBlueprint(activeWorkspace?.raidBlueprint),
    [activeWorkspace?.raidBlueprint],
  )

  // --------------------------------------------------------------------------
  // Target selection — every GOAL/TASK/SUBTASK is a valid boss now, not just
  // goals as before the checklist-books refactor.
  // --------------------------------------------------------------------------
  const targetOptions = useMemo(() => buildRaidTargetOptions(elements), [elements])
  const [targetId, setTargetId] = useState<string>(targetOptions[0]?.id ?? '')
  useEffect(() => {
    if (!targetId && targetOptions.length > 0) setTargetId(targetOptions[0].id)
  }, [targetOptions, targetId])

  const targetStats = useMemo(
    () => computeRaidTargetStats(targetId || null, elements, checklistItems),
    [targetId, elements, checklistItems],
  )
  const targetElement = elements.find((element) => element.id === targetId) ?? null

  // --------------------------------------------------------------------------
  // Forge form state.
  // --------------------------------------------------------------------------
  const [bossTitle, setBossTitle] = useState('Milestone Boss')
  const [isPenaltyRaid, setIsPenalty] = useState(false)
  const [enrageDays, setEnrageDays] = useState(blueprint.defaultEnrageDays)
  const [fundingCredits, setFundingCredits] = useState(blueprint.defaultFunding)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Auto-arm penalty when the target is heavy and the blueprint asks for it.
  useEffect(() => {
    if (!blueprint.autoPenaltyOnHighStakes || !targetElement) return
    if ((targetElement.weight ?? 0) >= 6) setIsPenalty(true)
  }, [blueprint.autoPenaltyOnHighStakes, targetElement])

  useEffect(() => {
    setEnrageDays(blueprint.defaultEnrageDays)
    setFundingCredits(blueprint.defaultFunding)
  }, [blueprint.defaultEnrageDays, blueprint.defaultFunding])

  const previewHp = previewBossHp(blueprint, targetStats.nestedTaskCount, targetStats.weightMass, targetStats.nestedChecklistCount)

  // --------------------------------------------------------------------------
  // Active raid derived data.
  // --------------------------------------------------------------------------
  const linkedElement = useMemo(
    () => (activeRaid ? elements.find((element) => element.id === activeRaid.targetElementId) ?? null : null),
    [activeRaid, elements],
  )
  const activeStats = useMemo(
    () => (activeRaid ? computeRaidTargetStats(activeRaid.targetElementId, elements, checklistItems) : null),
    [activeRaid, elements, checklistItems],
  )
  const sortedLeaderboard = useMemo(() => {
    if (!activeRaid) return []
    return Object.values(activeRaid.damageLeaderboard).sort((a, b) => b.damageDealt - a.damageDealt)
  }, [activeRaid])

  const curseActive = isCurseActive(activeCurse, new Date().toISOString())
  const victoryOverlayVisible = activeRaid ? activeRaid.currentBossHp <= 0 : false

  // --------------------------------------------------------------------------
  // Arena view switcher.
  // --------------------------------------------------------------------------
  const availableArenas: ArenaKey[] = useMemo(() => {
    const raidAllowed = activeRaid?.allowedRaidViews ?? blueprint.allowedViews
    return ['BATTLEFIELD', ...(['WAR_ROOM', 'CONSTELLATION', 'TIMELINE'] as RaidView[]).filter((view) => raidAllowed.includes(view))]
  }, [activeRaid, blueprint.allowedViews])

  const [selectedArena, setSelectedArena] = useState<ArenaKey>('BATTLEFIELD')
  useEffect(() => {
    if (!availableArenas.includes(selectedArena)) setSelectedArena(availableArenas[0] ?? 'BATTLEFIELD')
  }, [availableArenas, selectedArena])

  // --------------------------------------------------------------------------
  // Handlers.
  // --------------------------------------------------------------------------
  const handleStart = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!targetElement || !activeWorkspaceId) return
    setSubmitError(null)
    setBusy(true)
    try {
      const enrageDeadline = new Date(Date.now() + enrageDays * 86_400_000).toISOString()
      await startRaid({
        workspaceId: activeWorkspaceId,
        targetElementId: targetElement.id,
        bossTitle,
        isPenaltyRaid,
        defaultRaidView: blueprint.defaultView,
        allowedRaidViews: blueprint.allowedViews.length > 0 ? blueprint.allowedViews : [blueprint.defaultView],
        nestedTaskCount: targetStats.nestedTaskCount,
        weightMass: targetStats.weightMass,
        nestedChecklistCount: targetStats.nestedChecklistCount,
        enrageDeadlineTimestamp: enrageDeadline,
        fundingCredits,
      })
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('NLSExperienceRaidCouldNotStart'))
    } finally {
      setBusy(false)
    }
  }

  const handleResolve = async (isVictory: boolean) => {
    setSubmitError(null)
    setBusy(true)
    try {
      await resolveRaid(isVictory)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('NLSExperienceRaidCouldNotResolve'))
    } finally {
      setBusy(false)
    }
  }

  // --------------------------------------------------------------------------
  // Render helpers.
  // --------------------------------------------------------------------------
  const renderRail = () => {
    if (activeRaid) return renderActiveRail()
    return renderForgeRail()
  }

  const renderForgeRail = () => (
    <form className={styles.railForm} onSubmit={(event) => void handleStart(event)}>
      <div className={styles.railGroup}>
        <span className={styles.railEyebrow}>{t('NLSExperienceRaidTarget')}</span>
        {targetOptions.length === 0 ? (
          <p className={styles.arenaMuted}>
            {t('NLSExperienceRaidNoTargets')}
          </p>
        ) : (
          <Select<string>
            trigger="pill"
            value={targetId || null}
            placeholder={t('NLSExperienceRaidTargetPlaceholder')}
            options={targetOptions.map<SelectOption<string>>((option) => ({
              value: option.id,
              label: TARGET_KIND_LABEL(option),
              searchText: `${option.kind} ${option.title} ${option.parentTitle ?? ''}`,
            }))}
            onChange={setTargetId}
            ariaLabel={t('NLSExperienceRaidTargetAria')}
            searchable
          />
        )}
      </div>

      <div className={styles.railGroup}>
        <span className={styles.railEyebrow}>{t('NLSExperienceRaidBossTitle')}</span>
        <TextInput value={bossTitle} maxLength={64} onChange={setBossTitle} required />
      </div>

      <div className={styles.railGroup}>
        <div className={styles.railRow}>
          <label className={styles.railField}>
            <span>{t('NLSExperienceRaidEnrageDays')}</span>
            <NumberStepper
              variant="orbit"
              min={1}
              max={30}
              step={1}
              value={enrageDays}
              onChange={setEnrageDays}
              suffix="d"
              ariaLabel={t('NLSExperienceRaidEnrageDaysAria')}
            />
          </label>
          <label className={styles.railField}>
            <span>{t('NLSExperienceRaidFundingCredits')}</span>
            <NumberStepper
              variant="orbit"
              min={0}
              max={100000}
              step={10}
              value={fundingCredits}
              onChange={setFundingCredits}
              suffix="c"
              ariaLabel={t('NLSExperienceRaidFundingCreditsAria')}
            />
          </label>
        </div>
        <Checkbox
          checked={isPenaltyRaid}
          onChange={setIsPenalty}
          label={<span>{t('NLSExperienceRaidPenaltyLabel', { multiplier: PENALTY_RAID_REWARD_MULTIPLIER, credits: BOSS_CURSE_BREAK_CREDIT_COST })}</span>}
        />
      </div>

      <div className={styles.railHpPreview}>
        <div className={styles.railHpPreviewHead}>
          <span className={styles.railEyebrow}>{t('NLSExperienceRaidHpPreview')}</span>
          <strong>{previewHp}</strong>
        </div>
        <p className={styles.railHpPreviewFormula}>{describeHpFormula(targetStats, previewHp)}</p>
        <p className={styles.railHpPreviewFormula}>
          {t('NLSExperienceRaidSplit', { label: REWARD_SPLIT_LABELS[blueprint.rewardSplit] })}
        </p>
      </div>

      {submitError && <p className={styles.error}>{submitError}</p>}

      {canManageRaids ? (
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          icon={<LocalFireDepartmentRoundedIcon fontSize="inherit" />}
          disabled={!targetElement || busy}
          loading={busy}
        >
          {busy ? t('NLSExperienceRaidForging') : t('NLSExperienceRaidForgeBoss')}
        </Button>
      ) : (
        <p className={styles.arenaMuted}>
          {t('NLSExperienceRaidManageRequired')}
        </p>
      )}

    </form>
  )

  const renderActiveRail = () => (
    <div className={styles.railForm}>
      <div className={styles.railGroup}>
        <span className={styles.railEyebrow}>{t('NLSExperienceRaidArena')}</span>
        <ul className={styles.railArenaList}>
          {availableArenas.map((key) => {
            const meta = getRaidArenaMeta(key, t)
            const active = selectedArena === key
            return (
              <li key={key}>
                <Pressable
                  type="button"
                  aria-pressed={active}
                  className={`${styles.railArenaButton} ${active ? styles.railArenaButtonActive : ''}`}
                  onClick={() => setSelectedArena(key)}
                  aria-current={active}
                >
                  <span className={styles.railArenaIcon} aria-hidden="true">{ARENA_ICON[key]}</span>
                  <span className={styles.railArenaBody}>
                    <strong>{meta.label}</strong>
                    <em>{meta.description}</em>
                  </span>
                </Pressable>
              </li>
            )
          })}
        </ul>
      </div>

      {activeRaid && (
        <div className={styles.railHpPreview}>
          <div className={styles.railHpPreviewHead}>
            <span className={styles.railEyebrow}>{t('NLSExperienceRaidBoss')}</span>
            <strong>{activeRaid.bossTitle}</strong>
          </div>
          <p className={styles.railHpPreviewFormula}>
            {activeRaid.isPenaltyRaid ? t('NLSExperienceRaidPenaltyVictory', { multiplier: PENALTY_RAID_REWARD_MULTIPLIER }) : t('NLSExperienceRaidNormal')}
            {' · '}{t('NLSExperienceRaidPoolCredits', { credits: activeRaid.allocatedCreditPool })}
          </p>
          <p className={styles.railHpPreviewFormula}>
            {t('NLSExperienceRaidContributorSummary', {
              count: sortedLeaderboard.length,
              damage: sortedLeaderboard.reduce((sum, entry) => sum + entry.damageDealt, 0),
            })}
          </p>
        </div>
      )}

      {isAdmin && !victoryOverlayVisible && activeRaid && (
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => void handleResolve(false)}
        >
          {t('NLSExperienceRaidEndEarly')}
        </Button>
      )}

      {isAdmin && curseActive && (
        <Button
          variant="secondary"
          size="sm"
          icon={<BoltRoundedIcon fontSize="inherit" />}
          disabled
          title={t('NLSExperienceRaidBreakCurseTitle')}
        >
          {t('NLSExperienceRaidBreakCurse', { credits: BOSS_CURSE_BREAK_CREDIT_COST })}
        </Button>
      )}
    </div>
  )

  const renderStage = () => {
    if (!activeRaid || !activeStats) return renderIdleStage()
    const hpPercent = Math.max(0, Math.min(100, (activeRaid.currentBossHp / activeRaid.initialBossHp) * 100))
    const arenaProps = {
      activeRaid,
      target: linkedElement,
      stats: activeStats,
      hpPercent,
      sortedLeaderboard,
    }

    return (
      <>
        {curseActive && <RaidStatusBanner variant="CURSE" />}
        <div className={styles.stageHero}>
          <div className={styles.stageHeroHead}>
            <span className={styles.railEyebrow}>{t('NLSExperienceRaidCoop')}</span>
            <h2 className={styles.stageHeroTitle}>{activeRaid.bossTitle}</h2>
            <p className={styles.stageHeroSub}>
              {t('NLSExperienceRaidTargetLine', { title: linkedElement?.title ?? 'Unknown' })}
              {' · '}
              {getRaidArenaMeta(selectedArena, t).label}
            </p>
          </div>
          <EnrageTimer deadlineIso={activeRaid.enrageDeadlineTimestamp} />
        </div>

        <div className={styles.stageArena}>
          {renderRaidArena(selectedArena, arenaProps)}
        </div>

        {victoryOverlayVisible && (
          <RaidStatusBanner
            variant="VICTORY"
            isAdmin={isAdmin}
            busy={busy}
            onDeclareVictory={() => void handleResolve(true)}
            onEndAsFailure={() => void handleResolve(false)}
          />
        )}

        {submitError && <p className={styles.error}>{submitError}</p>}

        <RaidAuditLedger
          raid={activeRaid}
          history={activeWorkspaceId}
          t={t}
        />
      </>
    )
  }

  const renderIdleStage = () => (
    <>
      <div className={styles.stageHero}>
        <div className={styles.stageHeroHead}>
          <span className={styles.railEyebrow}>{t('NLSExperienceRaidCoop')}</span>
          <h2 className={styles.stageHeroTitle}>{t('NLSExperienceRaidForgeBossTitle')}</h2>
          <p className={styles.stageHeroSub}>
            {t('NLSExperienceRaidForgeBossBody')}
          </p>
        </div>
        <div className={styles.stagePreviewBadge}>
          <CenterFocusStrongRoundedIcon fontSize="inherit" />
          <span>{t('NLSExperienceRaidPreviewHp')}</span>
          <strong>{previewHp || 0}</strong>
        </div>
      </div>

      {targetElement ? (
        <div className={styles.stageTargetCard}>
          <span className={styles.stageTargetKind}>{targetElement.type}</span>
          <h3>{targetElement.title || t('NLSExperienceRaidUntitled')}</h3>
          {targetElement.description && (
            <p className={styles.stageTargetDescription}>{targetElement.description}</p>
          )}
          <div className={styles.stageTargetMeta}>
            <span>{t('NLSExperienceRaidTargetTasks', { count: targetStats.nestedTaskCount })}</span>
            <span>{t('NLSExperienceRaidTargetChecklists', { count: targetStats.nestedChecklistCount, done: targetStats.completedChecklistCount })}</span>
            <span>{t('NLSExperienceRaidTargetWeight', { weight: targetStats.weightMass.toFixed(1) })}</span>
          </div>
          <div className={styles.stageTargetProgress}>
            <div style={{ width: `${Math.round((targetElement.progress ?? 0) * 100)}%` }} />
          </div>
        </div>
      ) : (
        <div className={styles.stageTargetCard}>
          <span className={styles.stageTargetKind}>{t('NLSExperienceRaidEmpty')}</span>
          <h3>{t('NLSExperienceRaidNothingSelected')}</h3>
          <p className={styles.stageTargetDescription}>
            {t('NLSExperienceRaidChooseTarget')}
          </p>
        </div>
      )}

      <div className={styles.stageEducation}>
        <div>
          <span className={styles.railEyebrow}>{t('NLSExperienceRaidDamageHow')}</span>
          <ul>
            <li><strong>{blueprint.checklistDamage} DMG</strong> {t('NLSExperienceRaidDamageChecklist')}</li>
            <li><strong>{blueprint.taskDamage} DMG</strong> {t('NLSExperienceRaidDamageTask')}</li>
            <li><strong>{blueprint.focusDamage} DMG</strong> {t('NLSExperienceRaidDamageFocus')}</li>
          </ul>
        </div>
        <div>
          <span className={styles.railEyebrow}>{t('NLSExperienceRaidPayout')}</span>
          <p className={styles.arenaMuted}>
            {t('NLSExperienceRaidServerPayout', { label: REWARD_SPLIT_LABELS[blueprint.rewardSplit] })}
          </p>
        </div>
      </div>

      <PastRaidsSection
        workspaceId={activeWorkspaceId}
        emptyMessage={t('NLSExperienceRaidPastEmpty')}
      />
    </>
  )

  return (
    <AnimatePresence>
      <motion.div
        key="raid-shell"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
      >
        <RailPageShell
          variant="two-column"
          ariaLabel={t('NLSExperienceRaidAria')}
          className="raidExperienceShell"
          collapsible={false}
          identity={{ badge: 'R', title: t('NLSExperienceRaidTitle'), subtitle: t('NLSExperienceRaidSubtitle') }}
          auroraParticleCount={14}
          chrome={false}
          leftRail={<div className="raidExperienceRail">{renderRail()}</div>}
          rightStage={<div className="raidExperienceStage">{renderStage()}</div>}
        />
      </motion.div>
    </AnimatePresence>
  )
}

// ---------------------------------------------------------------------------
// RaidAuditLedger — inline component so the ledger stays close to the raid
// data it summarizes. If a second surface needs it, promote to its own file.
// ---------------------------------------------------------------------------

interface AuditEntry {
  id: string
  actor: string
  detail: string
  damage: number
  atIso: string
}

const RaidAuditLedger = ({ raid, history, t }: { raid: ActiveRaidModel; history: string | null; t: ExperienceNlsT }) => {
  void history
  const entries: AuditEntry[] = useMemo(() => {
    // Derive a synthetic audit stream from the current leaderboard snapshot:
    // one row per contributor sorted by damage, tagged with an "assault"
    // signature. Once the workspace activity feed is wired end-to-end, swap
    // this reducer for a real subscription.
    return Object.values(raid.damageLeaderboard)
      .sort((a, b) => b.damageDealt - a.damageDealt)
      .slice(0, 8)
      .map((entry, index) => ({
        id: `${raid.raidId}-${entry.userEmail}`,
        actor: entry.userDisplayName,
        detail: index === 0
          ? t('NLSExperienceRaidDamageDetailTop', { damage: entry.damageDealt })
          : t('NLSExperienceRaidDamageDetail', { damage: entry.damageDealt }),
        damage: entry.damageDealt,
        atIso: raid.enrageDeadlineTimestamp,
      }))
  }, [raid])

  return (
    <div className={styles.stageAuditLedger}>
      <div className={styles.stageAuditLedgerHeader}>
        <span className={styles.railEyebrow}>{t('NLSExperienceRaidLiveAudit')}</span>
        <strong>{t('NLSExperienceRaidFeed')}</strong>
        <HistoryRoundedIcon fontSize="inherit" />
      </div>
      {entries.length === 0 ? (
        <p className={styles.arenaMuted}>{t('NLSExperienceRaidDamageEmpty')}</p>
      ) : (
        <ol className={styles.stageAuditLedgerList}>
          {entries.map((entry) => (
            <li key={entry.id}>
              <strong>{entry.actor}</strong>
              <span>{entry.detail}</span>
              <em>{entry.damage} DMG</em>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
